import { useNavigate } from '@tanstack/react-router';
import { logout, getCurrentUser, getUserInitials } from '@utils/auth';
import { Button } from '@ui/button';
import { Avatar, AvatarFallback } from '@ui/avatar';
import { Badge } from '@ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/popover';
import { Separator } from '@ui/separator';
import { useEffect, useState } from 'react';
import { Settings } from 'lucide-react';
import { FilaConfigModal } from '../config-modal';
import { useVersionInfo } from '../VersionDisplay';

interface UserProfile {
  id: string;
  name: string;
  active: boolean;
}

interface UserDisplayData {
  name: string;
  email: string;
  profiles: UserProfile[];
}

// Função para padronizar nomes de perfis para o contexto de fila
function standardizeProfileName(profileName: string): string {
  const profileMap: Record<string, string> = {
    // Perfis específicos do sistema de fila
    'operador-fila': 'Operador de Fila',
    'gerente-fila': 'Gerente de Fila',
    atendente: 'Atendente',
    cozinha: 'Cozinha',
    caixa: 'Caixa',
    // Outros mapeamentos comuns
    admin: 'Administrador',
    administrator: 'Administrador',
    backoffice: 'Administrativo',
    manager: 'Gerente',
    employee: 'Funcionário',
    cashier: 'Operador de Caixa',
    supervisor: 'Supervisor',
    user: 'Usuário',
    owner: 'Proprietário',
    financial: 'Financeiro',
    support: 'Suporte',
    analyst: 'Analista',
    coordinator: 'Coordenador',
    director: 'Diretor',
  };

  const lowerName = profileName.toLowerCase().trim();
  const mapped = profileMap[lowerName];

  // Return mapped value or capitalize the first letter of original
  return mapped || profileName.charAt(0).toUpperCase() + profileName.slice(1).toLowerCase();
}

export function Header() {
  const navigate = useNavigate();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const versionInfo = useVersionInfo();

  // Dados mockados para fallback - manter compatibilidade com o sistema
  const [user, setUser] = useState<UserDisplayData>({
    name: 'Operador de Fila',
    email: 'operador@cantina.com',
    profiles: [
      {
        id: 'default',
        name: 'Operador de Fila',
        active: true,
      },
    ],
  });

  const handleLogout = async () => {
    try {
      await logout();
      navigate({ to: '/login' });
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      // Force navigation even if logout fails
      navigate({ to: '/login' });
    }
  };

  const handleConfigModalClose = (value: boolean) => {
    setShowConfigModal(value);
    if (!value) {
      window.location.reload();
    }
  };

  // Carregar dados do usuário quando o componente montar
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await getCurrentUser();
        if (userData) {
          const profiles: UserProfile[] = [
            {
              id: 'legacy',
              name: standardizeProfileName(userData.role),
              active: true,
            },
          ];

          setUser({
            name: userData.name,
            email: userData.email,
            profiles: profiles,
          });
        }
      } catch (error) {
        console.warn('Failed to load user data:', error);
        // Manter dados mockados como fallback
      }
    };

    loadUserData();
  }, []);

  const userInitials = getUserInitials(user.name);

  return (
    <div className="border-b border-brand-primary-100 bg-gradient-to-b from-brand-primary to-brand-secondary shadow-sm">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Logo e Título */}
        <div className="flex items-center space-x-4">
          <div className="text-white">
            <h1 className="text-2xl font-bold">Fila Digital - Pedidos da fila</h1>
            <p className="text-white/80 text-xs">
              CantinaPay - {versionInfo.version} (Build #{versionInfo.buildNumber})
            </p>
          </div>
        </div>

        {/* Área direita - Botão Config + Avatar */}
        <div className="flex items-center space-x-2">
          {/* Botão de Configurações */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowConfigModal(true)}
            className="hover:bg-white/10 focus:ring-white/30 text-white transition-all duration-200 focus:ring-2">
            <Settings className="h-4 w-4" />
          </Button>

          {/* User Avatar with Popover */}
          <div className="flex items-center">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="hover:bg-white/10 focus:ring-white/30 relative h-10 w-10 rounded-full transition-all duration-200 focus:ring-2"
                  size="sm">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="border-white/20 border-2 bg-white font-medium text-brand-primary">{userInitials}</AvatarFallback>
                  </Avatar>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 border-brand-primary-100 p-0 shadow-lg" align="end">
                <div className="px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-brand-primary font-medium text-white">{userInitials}</AvatarFallback>
                    </Avatar>
                    <div className="space-y-1">
                      <p className="text-sm font-medium leading-none">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      <div className="flex flex-wrap gap-1">
                        {user.profiles.map((profile) => (
                          <Badge
                            key={profile.id}
                            variant="ghost"
                            className={`h-auto px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                              profile.active ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-700'
                            }`}>
                            {profile.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
                <div className="p-1">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm font-normal text-gray-600 transition-colors hover:bg-brand-primary-50 hover:text-brand-primary-600"
                    onClick={() => {
                      /* Navigate to settings when route is created */
                    }}>
                    Configurações
                  </Button>
                  <Separator className="my-1" />
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm font-normal text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
                    onClick={handleLogout}>
                    Sair
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Configuration Modal */}
      <FilaConfigModal open={showConfigModal} onOpenChange={handleConfigModalClose} />
    </div>
  );
}
